/**
 * Clearance Config Utility Functions
 * Helper functions for looking up category and option labels from clearance config
 *
 * Data structure for clearing values:
 * {
 *   categoryId: {
 *     labelId: "label-uuid",
 *     untilDate: 1234567890 | null  // Only set if option.hasUntilDate is true
 *   }
 * }
 */

import { generateRandomColor } from './color.utils';
import { LIVE_VIEW_TYPE, POST_VIEW_TYPE, DETAIL_VIEW_TYPE } from '../constants/clearance';

/**
 * Default labels to be created for every new category
 */
export const DEFAULT_CATEGORY_LABELS = [
    {
        label: 'To be confirmed',
        color: '#6b7280',
        editable: true,
        defaultSelected: true
    },
    {
        label: 'After approval',
        color: '#eab308',
        editable: true,
        defaultSelected: false
    },
    {
        label: 'Limited',
        color: '#f59e0b',
        editable: true,
        defaultSelected: false
    },
    {
        label: 'No',
        color: '#ef4444',
        editable: true,
        defaultSelected: false
    },
    {
        label: 'Yes',
        color: '#22c55e',
        editable: true,
        defaultSelected: false
    },
    {
        label: 'Hidden',
        color: '#9ca3af',
        editable: false,
        defaultSelected: false
    }
];

/**
 * Create a new label object with default properties
 * @returns {Object} New label object with unique ID and random color
 */
export function createNewLabel() {
    return {
        id: `new-label-${Date.now()}`,
        label: '',
        color: generateRandomColor(),
        editable: true,
        defaultSelected: false
    };
}

/**
 * Create a new category object with default properties and labels
 * @returns {Object} New category object with unique ID and default labels
 */
export function createNewCategory() {
    const timestamp = Date.now();
    return {
        id: `new-${timestamp}`,
        label: 'New Category',
        position: 0,
        visibleInViews: [LIVE_VIEW_TYPE, POST_VIEW_TYPE, DETAIL_VIEW_TYPE],
        visibleByDefault: false,
        labels: DEFAULT_CATEGORY_LABELS.map((labelTemplate, index) => ({
            id: `new-label-${index}`,
            ...labelTemplate,
            createdAt: new Date().toISOString()
        }))
    };
}

/**
 * Get category label by category ID
 * @param {Object} categoryConfig - The category configuration object
 * @param {string} fieldId - The category ID to look up
 * @returns {string} Field label or empty string if not found (deleted category)
 */
export function getFieldLabel(categoryConfig, fieldId) {
    if (!categoryConfig || !categoryConfig.categories) {
        return '';
    }
    const category = categoryConfig.categories.find(f => f.id === fieldId);
    return category ? category.label : '';
}

/**
 * Extract labelId from a category value (handles both old flat and new nested structure)
 * @param {Object|string} categoryValue - The value stored for a category
 * @returns {string} The label ID
 */
export function extractLabelId(categoryValue) {
    if (!categoryValue) return '';
    if (typeof categoryValue === 'object') {
        return categoryValue.labelId || '';
    }
    return categoryValue;
}

/**
 * Get full option details by category ID and option ID
 * @param {Object} categoryConfig - The category configuration object
 * @param {string} fieldId - The category ID
 * @param {string|Object} categoryValue - The option ID or category value object
 * @returns {Object|null} Option object with label, color, hasUntilDate, etc.
 */
export function getOption(categoryConfig, fieldId, categoryValue) {
    const labelId = extractLabelId(categoryValue);
    if (!categoryConfig || !categoryConfig.categories || !labelId) {
        return null;
    }
    const category = categoryConfig.categories.find(f => f.id === fieldId);
    if (!category || !category.labels) {
        return null;
    }
    return category.labels.find(label => label.id === labelId) || null;
}

const getValueByPath = (obj, path = '') => path.split('.').reduce((current, prop) => current?.[prop], obj);

const normalizeSortValue = value => {
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'string') return value.toLowerCase();
    return value;
};

const sortList = (items, sortKey, sortDirection = 'asc') => {
    if (!sortKey || !items?.length) return items;

    return [...items].sort((a, b) => {
        let aValue = normalizeSortValue(getValueByPath(a, sortKey));
        let bValue = normalizeSortValue(getValueByPath(b, sortKey));

        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';

        if (aValue < bValue) return sortDirection === 'desc' ? 1 : -1;
        if (aValue > bValue) return sortDirection === 'desc' ? -1 : 1;
        return 0;
    });
};

/**
 * Filter categories based on search query
 * @param {Array} categories - Array of category objects to filter
 * @param {string} query - Search query string
 * @returns {Array} Filtered array of categories
 */
export function filterCategories(categories, query) {
    if (!query || query.trim() === '') return categories;

    const searchTerm = query.toLowerCase().trim();

    return categories.filter(item => {
        if (item.label && item.label.toLowerCase().includes(searchTerm)) {
            return true;
        }
        if (item.labels && Array.isArray(item.labels)) {
            if (item.labels.some(label => label.label && label.label.toLowerCase().includes(searchTerm))) {
                return true;
            }
        }
        return false;
    });
}

/**
 * Sort categories by a given key and direction
 * @param {Array} categories - Array of category objects to sort
 * @param {string} sortKey - The key to sort by (supports nested paths like 'user.name')
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted array of categories
 */
export function sortCategories(categories, sortKey, sortDirection = 'asc') {
    return sortList(categories, sortKey, sortDirection);
}

export function sortLabels(labels, sortKey, sortDirection = 'asc') {
    return sortList(labels, sortKey, sortDirection);
}

export function filterLabels(labels, query) {
    if (!labels) return [];
    if (!query || query.trim() === '') return labels;

    const searchTerm = query.toLowerCase().trim();
    return labels.filter(label => label.label?.toLowerCase().includes(searchTerm));
}

/**
 * Get categories visible in a specific view type
 * @param {Array} categories - Array of category objects
 * @param {string} viewType - View type to filter by (e.g., 'LIVE', 'POST')
 * @returns {Array} Filtered array of categories visible in the specified view
 */
export function getCategoriesForView(categories, viewType) {
    if (!categories || !viewType) return [];

    return categories.filter(category => category.visibleInViews && category.visibleInViews.includes(viewType));
}

/**
 * Apply default labels to clearing data for categories that don't have a label set
 * @param {Array} clearing - Array of partner clearing objects
 * @param {Object} categoryConfig - The category configuration with categories and labels
 * @returns {Array} Updated clearing array with default labels applied
 */
export function applyDefaultLabelsToClearing(clearing, categoryConfig) {
    if (!clearing || !categoryConfig || !categoryConfig.categories) {
        return clearing;
    }

    return clearing.map(partnerClearing => {
        const updatedClearing = { ...partnerClearing };

        categoryConfig.categories.forEach(category => {
            const categoryValue = updatedClearing[category.id];
            const hasLabel = categoryValue && categoryValue.labelId;

            if (!hasLabel) {
                const defaultLabel = category.labels?.find(l => l.defaultSelected);
                if (defaultLabel) {
                    updatedClearing[category.id] = {
                        labelId: defaultLabel.id,
                        untilDate: null
                    };
                }
            }
        });

        return updatedClearing;
    });
}
